import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';

// Panier du rapport composé (DOC-17), un par conversation.
//
// Le contenu et sa synchronisation avec le serveur (BE-09 : panier rattaché à la conversation,
// retrouvé d'un poste à l'autre) viennent de useReportBasketSync, appelé par l'application
// (qui en a besoin à la suppression d'une conversation) et passé ici en `basket`.
// Ce fournisseur ajoute les actions du panier et la fenêtre (ouverture, focus au retour).

const BasketContext = createContext({
  items: [],
  sync: 'local',
  has: () => false,
  toggle: () => {},
  remove: () => {},
  move: () => {},
  clear: () => {},
  drawerOpen: false,
  openDrawer: () => {},
  closeDrawer: () => {},
});

/**
 * @param {{basket: {scope: string, items: object[], sync: string, dispatch: (action) => void}}} props
 *   basket : valeur renvoyée par useReportBasketSync.
 */
export function ReportBasketProvider({ basket, children }) {
  const { scope, items, sync, dispatch } = basket;
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerScope, setDrawerScope] = useState(scope);
  const triggerRef = useRef(null);

  // Autre conversation : la fenêtre se ferme, sauf au premier envoi (le panier suit)
  if (drawerScope !== scope) {
    setDrawerScope(scope);
    if (!(drawerScope?.startsWith('local:') && scope?.startsWith('c:'))) setDrawerOpen(false);
  }

  const has = useCallback((key) => items.some((i) => i.key === key), [items]);
  const toggle = useCallback((item) => dispatch({ type: 'toggle', item }), [dispatch]);
  const remove = useCallback((key) => dispatch({ type: 'remove', key }), [dispatch]);
  const move = useCallback((key, to) => dispatch({ type: 'move', key, to }), [dispatch]);
  const clear = useCallback(() => dispatch({ type: 'clear' }), [dispatch]);

  const openDrawer = useCallback((trigger = null) => {
    triggerRef.current = trigger ?? (typeof document !== 'undefined' ? document.activeElement : null);
    setDrawerOpen(true);
  }, []);

  // Le focus revient au bouton d'ouverture (en-tête), s'il est toujours affiché
  const closeDrawer = useCallback(() => {
    setDrawerOpen(false);
    const trigger = triggerRef.current;
    requestAnimationFrame(() => {
      if (trigger?.isConnected) trigger.focus({ preventScroll: true });
    });
  }, []);

  const value = useMemo(
    () => ({ items, sync, has, toggle, remove, move, clear, drawerOpen, openDrawer, closeDrawer }),
    [items, sync, has, toggle, remove, move, clear, drawerOpen, openDrawer, closeDrawer]
  );

  return <BasketContext.Provider value={value}>{children}</BasketContext.Provider>;
}

export const useReportBasket = () => useContext(BasketContext);
